import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import AdvocateService from '../../services/advocate';
import AuthService from '../../services/auth';
import {
  AdvocateProfile,
  AdvocateEntitlementProgram,
} from '../../models/advocate/types';
import { redirectToLogin } from '../../lib/auth';
import { AxiosError } from 'axios';
import { useEffect } from 'react';

/**
 * Query key for advocate data
 */
export const ADVOCATE_QUERY_KEY = ['advocate'];

/**
 * Fetch advocate data from mojo
 *
 * @param advocateId - The ID of the advocate to fetch
 * @param options - Optional React Query options
 * @returns Query result with advocate data
 */
export const useAdvocateQuery = (
  advocateId: number,
  options?: Omit<
    UseQueryOptions<AdvocateProfile, Error, AdvocateProfile, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<AdvocateProfile, Error, AdvocateProfile, string[]>({
    queryKey: [...ADVOCATE_QUERY_KEY, advocateId.toString()],
    queryFn: async () => {
      const response = await new AdvocateService(advocateId).fetch();
      return response.data.data.profile;
    },
    keepPreviousData: true,
    ...options,
  });
};

/**
 * Fetch advocate entitlements from mojo
 *
 * @param advocateEmail - The email of the advocate
 * @param options - Optional React Query options
 * @returns Query result with advocate entitlements
 */
export const useAdvocateEntitlementsQuery = (
  advocateEmail: string,
  options?: Omit<
    UseQueryOptions<
      AdvocateEntitlementProgram[],
      Error,
      AdvocateEntitlementProgram[],
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<
    AdvocateEntitlementProgram[],
    Error,
    AdvocateEntitlementProgram[],
    string[]
  >({
    queryKey: ['advocateEntitlements', advocateEmail],
    queryFn: async () => {
      const response = await new AuthService().fetchEntitlements(advocateEmail);
      return response.data.programs;
    },
    enabled: !!advocateEmail,
    keepPreviousData: true,
    ...options,
  });

  useEffect(() => {
    if (query.error && query.error instanceof AxiosError) {
      if (
        query.error.response?.status === 401 ||
        query.error.response?.status === 403
      ) {
        redirectToLogin();
      }
    }
  }, [query.error]);

  return query;
};
